import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import {
  BgColorsOutlined,
  ColumnHeightOutlined,
  DragOutlined,
  EyeOutlined,
  FileOutlined,
  FileTextOutlined,
  FontSizeOutlined,
  LoadingOutlined,
  ReloadOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { BlobProvider, PDFViewer, usePDF } from "@react-pdf/renderer";
import {
  Button,
  Card,
  ColorPicker,
  Divider,
  Modal,
  Segmented,
  Select,
  Slider,
  Space,
  Spin,
  Switch,
  Tabs,
  Tooltip,
  Typography,
  message,
} from "antd";
import { AVAILABLE_FONTS } from "../../Services/ReactPDFService.jsx";
import PrintableResume from "@/Pages/ResumeView/PrintableResume";
import { sortResumeForDisplay } from "@/Utils/DateSortingUtils";
import { prepareResumeDataForPDF } from "@/Utils/Resume/ResumeDataManager";
import {
  DEFAULT_SECTION_ORDER,
  SECTION_METADATA,
  clearCustomSectionOrder,
  getOrderedSections,
  getSectionOrder,
  loadCustomSectionOrder,
  reorderSections,
  saveCustomSectionOrder,
} from "@/Utils/SectionOrderingUtils";
import ClassicATSTemplate from "../../Templates/ClassicATS";
import ClassicCoverLetter from "../../Templates/CoverLetter/ClassicCoverLetter";
import CreativeCoverLetter from "../../Templates/CoverLetter/CreativeCoverLetter";
import ModernCoverLetter from "../../Templates/CoverLetter/ModernCoverLetter";
import PhotoCoverLetter from "../../Templates/CoverLetter/PhotoCoverLetter";
import SimpleBlushCoverLetter from "../../Templates/CoverLetter/SimpleBlushCoverLetter";
import ModernSlateCoverLetter from "../../Templates/CoverLetter/ModernSlateCoverLetter";
import ProfessionalBlueCoverLetter from "../../Templates/CoverLetter/ProfessionalBlueCoverLetter";
import ObsidianEdgeCoverLetter from "../../Templates/CoverLetter/ObsidianEdgeCoverLetter";
import CleanProfessionalCoverLetter from "../../Templates/CoverLetter/CleanProfessionalCoverLetter";
import MinimalistProCoverLetter from "../../Templates/CoverLetter/MinimalistProCoverLetter";
import PhotoClassicCoverLetter from "../../Templates/CoverLetter/PhotoClassicCoverLetter";
import ExecutiveSidebarCoverLetter from "../../Templates/CoverLetter/ExecutiveSidebarCoverLetter";
import ExecutiveTemplate from "../../Templates/Executive";
import PhotoTemplate from "../../Templates/PhotoTemplate";
import ProfessionalTemplate from "../../Templates/Professional";
import SimpleBlushTemplate from "../../Templates/SimpleBlush";
import ModernSlateTemplate from "../../Templates/ModernSlate";
import ProfessionalBlueTemplate from "../../Templates/ProfessionalBlue";
import ObsidianEdgeTemplate from "../../Templates/ObsidianEdge";
import CleanProfessionalTemplate from "../../Templates/CleanProfessional";
import MinimalistProTemplate from "../../Templates/MinimalistPro";
import PhotoClassicTemplate from "../../Templates/PhotoClassic";
import ExecutiveSidebarTemplate from "../../Templates/ExecutiveSidebar";
import TraditionalTemplate from "../../Templates/Traditional";
import ATSPrimeTemplate from "../../Templates/ATSPrime";
import SimpleATSTemplate from "../../Templates/SimpleATS";
import TraditionalCoverLetter from "../../Templates/CoverLetter/TraditionalCoverLetter";
import ATSPrimeCoverLetter from "../../Templates/CoverLetter/ATSPrimeCoverLetter";
import SimpleATSCoverLetter from "../../Templates/CoverLetter/SimpleATSCoverLetter";
import ProfilePhotoUpload from "./ProfilePhotoUpload";
import ResumePreviewSkeleton from "./ResumePreviewSkeleton";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// Map template IDs to react-pdf components
const TEMPLATE_MAPPING = {
  classic: ClassicATSTemplate,
  modern: ProfessionalTemplate,
  creative: ExecutiveTemplate,
  photo: PhotoTemplate,
  simple_blush: SimpleBlushTemplate,
  modern_slate: ModernSlateTemplate,
  professional_blue: ProfessionalBlueTemplate,
  obsidian_edge: ObsidianEdgeTemplate,
  clean_professional: CleanProfessionalTemplate,
  minimalist_pro: MinimalistProTemplate,
  photo_classic: PhotoClassicTemplate,
  executive_sidebar: ExecutiveSidebarTemplate,
  traditional: TraditionalTemplate,
  ats_prime: ATSPrimeTemplate,
  simple_ats: SimpleATSTemplate,
};

// Map template IDs to cover letter components
const COVER_LETTER_TEMPLATE_MAPPING = {
  classic: ClassicCoverLetter,
  modern: ModernCoverLetter,
  creative: CreativeCoverLetter,
  photo: PhotoCoverLetter,
  simple_blush: SimpleBlushCoverLetter,
  modern_slate: ModernSlateCoverLetter,
  professional_blue: ProfessionalBlueCoverLetter,
  obsidian_edge: ObsidianEdgeCoverLetter,
  clean_professional: CleanProfessionalCoverLetter,
  minimalist_pro: MinimalistProCoverLetter,
  photo_classic: PhotoClassicCoverLetter,
  executive_sidebar: ExecutiveSidebarCoverLetter,
  traditional: TraditionalCoverLetter,
  ats_prime: ATSPrimeCoverLetter,
  simple_ats: SimpleATSCoverLetter,
};

const CustomizableResumePreview = forwardRef(
  (
    {
      resumeData,
      selectedTemplate,
      resumeId,
      onClose,
      onPrint,
      onDownload,
      onStylesUpdate,
      preloadedUrl,
      onCustomizationChange,
      onSectionOrderChange,
      onPhotoUploaded,
    },
    ref
  ) => {
    const [customizeMode, setCustomizeMode] = useState(true);
    const [orderedSections, setOrderedSections] = useState([]);
    const [customSectionOrder, setCustomSectionOrder] = useState(null);
    const [activeTab, setActiveTab] = useState("1");
    const [cachedPDFs, setCachedPDFs] = useState({});
    const [viewMode, setViewMode] = useState("resume"); // 'resume' or 'coverLetter'
    const [profilePhotoUrl, setProfilePhotoUrl] = useState(resumeData?.profilePhoto?.url || resumeData?.profilePhotoUrl || null);

    // Customization state - template specific
    const [customizations, setCustomizations] = useState({});

    // Get template-specific default colors
    const getTemplateDefaults = () => {
      const templateId = selectedTemplate?.id || "default";


      const commonDefaults = { fontSize: 14, sectionSpacing: 7, fontFamily: "Roboto" };

      switch (templateId) {
        case "modern": // Professional Template
          return { ...commonDefaults, titleColor: "#2c3e50", subtitleColor: "#7f8c8d" };
        case "creative": // Executive Template
          return { ...commonDefaults, titleColor: "#1a202c", subtitleColor: "#4a5568" };
        case "photo": // Photo Template
          return { ...commonDefaults, titleColor: "#3C6C93", subtitleColor: "#3C6C93" };
        case "simple_blush": // Simple Blush Template
          return { ...commonDefaults, titleColor: "#2d3748", subtitleColor: "#e8b4b8" };
        case "modern_slate": // Modern Slate Template
          return { ...commonDefaults, titleColor: "#1e293b", subtitleColor: "#334155" };
        case "professional_blue": // Professional Blue Template
          return { ...commonDefaults, titleColor: "#3b5998", subtitleColor: "#2c3e50" };
        case "obsidian_edge": // Obsidian Edge Template
          return { ...commonDefaults, titleColor: "#1a1a1a", subtitleColor: "#444444" };
        case "clean_professional": // Clean Professional Template
          return { ...commonDefaults, titleColor: "#000000", subtitleColor: "#666666" };
        case "minimalist_pro": // Minimalist Pro Template
          return { ...commonDefaults, titleColor: "#0d9488", subtitleColor: "#6b7280" };
        case "photo_classic": // Photo Classic Template
          return { ...commonDefaults, titleColor: "#1a1a1a", subtitleColor: "#444444" };
        case "executive_sidebar": // Executive Sidebar Template
          return { ...commonDefaults, titleColor: "#2d3e50", subtitleColor: "#4b5563" };
        case "traditional": // Traditional Template
          return { ...commonDefaults, titleColor: "#1a1a1a", subtitleColor: "#333333" };
        case "ats_prime": // ATS Prime Template
          return { ...commonDefaults, titleColor: "#2563eb", subtitleColor: "#1a1a1a" };
        case "simple_ats": // Simple ATS Template
          return { ...commonDefaults, titleColor: "#3b82f6", subtitleColor: "#6b7280" };
        case "classic": // ClassicATS Template
        default:
          return { ...commonDefaults, titleColor: "#1a1a1a", subtitleColor: "#333333" };
      }
    };

    // Get current template customizations
    const getTemplateCustomizations = () => {
      const templateId = selectedTemplate?.id || "default";
      return customizations[templateId] || getTemplateDefaults();
    };

    // Update template-specific customizations
    const updateTemplateCustomization = (key, value) => {
      const templateId = selectedTemplate?.id || "default";
      setCustomizations((prev) => {
        const newCustomizations = {
          ...prev,
          [templateId]: {
            ...getTemplateCustomizations(),
            [key]: value,
          },
        };

        // Notify parent component about customization changes
        if (onCustomizationChange) {
          onCustomizationChange(newCustomizations[templateId]);
        }

        return newCustomizations;
      });
    };

    const { fontSize, sectionSpacing, titleColor, subtitleColor, fontFamily } = getTemplateCustomizations();

    // Initialize sections when component mounts or resume data changes
    useEffect(() => {
      if (resumeData?.parsedData) {
        const sortedData = sortResumeForDisplay(resumeData.parsedData);

        // Load saved custom order if exists
        const savedOrder = loadCustomSectionOrder(resumeId);
        // Pass custom section titles to display user-defined names in the reordering UI
        const customTitles = resumeData.parsedData.sectionTitles || {};
        const sections = getOrderedSections(sortedData, savedOrder, customTitles);

        setOrderedSections(sections);
        setCustomSectionOrder(savedOrder);
      }
    }, [resumeData, resumeId]);

    // Notify parent of initial customizations when component mounts or template changes
    useEffect(() => {
      if (selectedTemplate) {
        const initialCustomizations = getTemplateCustomizations();
        if (onCustomizationChange) {
          onCustomizationChange(initialCustomizations);
        }
      }
    }, [selectedTemplate, onCustomizationChange]);

    // Notify parent of initial section order when sections are loaded
    useEffect(() => {
      if (customSectionOrder && onSectionOrderChange) {
        onSectionOrderChange(customSectionOrder);
      }
    }, [customSectionOrder, onSectionOrderChange]);

    // Load template-specific customizations on template change
    useEffect(() => {
      if (selectedTemplate?.id) {
        const templateId = selectedTemplate.id;
        const storageKey = `resume_customizations_${resumeId}_${templateId}`;
        const saved = localStorage.getItem(storageKey);

        if (saved) {
          try {
            const savedCustomizations = JSON.parse(saved);
            setCustomizations((prev) => ({
              ...prev,
              [templateId]: savedCustomizations,
            }));
          } catch (error) {
            console.error("Failed to load saved customizations:", error);
            // If loading fails, set template defaults
            setCustomizations((prev) => ({
              ...prev,
              [templateId]: getTemplateDefaults(),
            }));
          }
        } else {
          // If no saved customizations, initialize with template defaults
          setCustomizations((prev) => ({
            ...prev,
            [templateId]: getTemplateDefaults(),
          }));
        }
      }
    }, [selectedTemplate?.id, resumeId]);

    // Save template-specific customizations to localStorage
    useEffect(() => {
      if (selectedTemplate?.id && customizations[selectedTemplate.id]) {
        const templateId = selectedTemplate.id;
        const storageKey = `resume_customizations_${resumeId}_${templateId}`;

        try {
          localStorage.setItem(storageKey, JSON.stringify(customizations[templateId]));
        } catch (error) {
          console.error("Failed to save customizations:", error);
        }
      }
    }, [customizations, selectedTemplate?.id, resumeId]);

    // Update profile photo URL when resumeData changes
    useEffect(() => {
      const photoUrl = resumeData?.profilePhoto?.url || resumeData?.profilePhotoUrl;
      if (photoUrl) {
        setProfilePhotoUrl(photoUrl);
      }
    }, [resumeData?.profilePhoto?.url, resumeData?.profilePhotoUrl]);

    // Handle photo upload callback
    const handlePhotoUploaded = (newPhotoUrl) => {
      setProfilePhotoUrl(newPhotoUrl);
      // Call parent callback if provided
      if (onPhotoUploaded) {
        onPhotoUploaded(newPhotoUrl);
      }
    };

    // Update parent component when styles change
    useEffect(() => {
      if (onStylesUpdate) {
        const currentCustomizations = getTemplateCustomizations();
        onStylesUpdate(currentCustomizations, customSectionOrder);
      }
    }, [customizations, selectedTemplate?.id, customSectionOrder, onStylesUpdate]);

    // Cache management - cleanup old cached PDFs to prevent memory issues
    useEffect(() => {
      const cacheKeys = Object.keys(cachedPDFs);
      if (cacheKeys.length > 5) {
        // Keep only 5 most recent cached PDFs
        const keysToRemove = cacheKeys.slice(0, cacheKeys.length - 5);
        keysToRemove.forEach((key) => {
          if (cachedPDFs[key]) {
            URL.revokeObjectURL(cachedPDFs[key]);
          }
        });
        setCachedPDFs((prev) => {
          const newCache = { ...prev };
          keysToRemove.forEach((key) => delete newCache[key]);
          return newCache;
        });
      }
    }, [cachedPDFs]);

    // Cleanup cached PDFs on unmount
    useEffect(() => {
      return () => {
        Object.values(cachedPDFs).forEach((url) => {
          if (url) URL.revokeObjectURL(url);
        });
      };
    }, []);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      handlePrint: handlePrintWithCustomStyles,
      getCustomizations: () => getTemplateCustomizations(),
      getSectionOrder: () => orderedSections.map((section) => section.id),
    }));

    // Reset all customizations to template defaults
    const handleReset = () => {
      if (!resumeData?.parsedData) return;

      const templateId = selectedTemplate?.id || 'default';

      // Reset section order
      const sortedData = sortResumeForDisplay(resumeData.parsedData);
      // Pass custom section titles to maintain user-defined names even after reset
      const customTitles = resumeData.parsedData.sectionTitles || {};
      const defaultSections = getOrderedSections(sortedData, DEFAULT_SECTION_ORDER, customTitles);
      setOrderedSections(defaultSections);
      setCustomSectionOrder(null);
      clearCustomSectionOrder(resumeId);

      // Reset all customizations to template defaults
      const templateDefaults = getTemplateDefaults();
      setCustomizations(prev => ({
        ...prev,
        [templateId]: templateDefaults
      }));

      // Clear localStorage for this template
      const storageKey = `resume_customizations_${resumeId}_${templateId}`;
      localStorage.removeItem(storageKey);

      // Notify parent
      if (onCustomizationChange) {
        onCustomizationChange(templateDefaults);
      }
      if (onSectionOrderChange) {
        onSectionOrderChange(null);
      }
    };

    // Handle drag end
    const handleDragEnd = (result) => {
      if (!result.destination) return;

      const { source, destination } = result;

      if (source.index === destination.index) return;

      // Prevent moving personalInfo (it should always be first)
      const draggedSection = orderedSections[source.index];
      if (draggedSection.id === "personalInfo" || destination.index === 0) {
        message.warning("Personal Information must stay at the top");
        return;
      }

      const newOrderedSections = reorderSections(orderedSections, source.index, destination.index);

      setOrderedSections(newOrderedSections);

      // Save to localStorage
      const newSectionOrder = getSectionOrder(newOrderedSections);
      setCustomSectionOrder(newSectionOrder);
      saveCustomSectionOrder(resumeId, newSectionOrder);

      // Notify parent component about section order changes
      if (onSectionOrderChange) {
        onSectionOrderChange(newSectionOrder);
      }
    };

    // Handle print with custom styles
    const handlePrintWithCustomStyles = () => {
      try {
        // Get the current resume preview content
        const resumePreviewElement = document.querySelector(".customizable-resume-preview .printable-resume");

        if (!resumePreviewElement) {
          message.error("Resume preview not found. Please try again.");
          return;
        }

        // Clone the resume content to ensure we have clean HTML
        const clonedResume = resumePreviewElement.cloneNode(true);

        // Create a hidden iframe for printing
        const iframe = document.createElement("iframe");
        iframe.style.position = "absolute";
        iframe.style.top = "-10000px";
        iframe.style.left = "-10000px";
        iframe.style.width = "8.5in";
        iframe.style.height = "11in";
        document.body.appendChild(iframe);

        // Create complete HTML for the iframe
        const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Resume - ${selectedTemplate?.name || "Preview"}</title>
          <style>
            @page {
              margin: 0.5in;
              size: letter;
              orphans: 2;
              widows: 2;
            }
            
            @page:first {
              margin: 0.5in;
            }
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            html, body {
              width: 100%;
              height: auto;
              margin: 0;
              padding: 0;
              background: white;
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            
            body {
              font-family: ${
                selectedTemplate?.id === "classic"
                  ? "Times, serif"
                  : selectedTemplate?.id === "modern"
                  ? "Arial, sans-serif"
                  : "Georgia, serif"
              };
              font-size: ${fontSize}px;
              line-height: 1.4;
              color: black;
              orphans: 2;
              widows: 2;
            }
            
            .printable-resume {
              width: 100%;
              margin: 0;
              padding: 0;
              background: white;
            }
            
            .section {
              margin-bottom: ${sectionSpacing}px;
              page-break-inside: avoid;
              orphans: 2;
              widows: 2;
            }
            
            .section-title {
              color: ${titleColor};
              font-size: ${fontSize * 1.1}px;
              font-weight: bold;
              text-transform: uppercase;
              margin-bottom: 8px;
            }
            
            .section-content {
              font-size: ${fontSize * 0.9}px;
            }
            
            .header {
              text-align: center;
              margin-bottom: ${sectionSpacing}px;
              padding-bottom: 12px;
              page-break-after: avoid;
            }
            
            .header h1 {
              font-size: ${fontSize * 1.4}px;
              font-weight: bold;
              margin-bottom: 8px;
              color: ${titleColor};
            }
            
            .header .contact {
              font-size: ${fontSize * 0.85}px;
              color: #666;
            }
            
            .experience-item,
            .education-item {
              margin-bottom: ${sectionSpacing * 0.75}px;
              page-break-inside: avoid;
            }
            
            .experience-title,
            .education-title {
              font-size: ${fontSize * 0.95}px;
              font-weight: bold;
              color: ${subtitleColor};
              margin-bottom: 4px;
            }
            
            .experience-company,
            .education-school {
              font-size: ${fontSize * 0.85}px;
              color: ${subtitleColor};
              margin-bottom: 4px;
            }
            
            .experience-date,
            .education-date {
              font-size: ${fontSize * 0.85}px;
              color: #666;
              margin-bottom: 8px;
            }
            
            .experience-description {
              font-size: ${fontSize * 0.8}px;
              line-height: 1.4;
              text-align: justify;
            }
            
            .skills-list {
              font-size: ${fontSize * 0.9}px;
              line-height: 1.1;
              display: grid !important;
              grid-template-columns: 1fr 1fr 1fr !important;
              gap: 2px 8px !important;
              column-gap: 8px !important;
              margin: 0;
            }
            
            .skill-item {
              margin: 0;
              padding: 0;
              display: flex;
              align-items: flex-start;
              word-wrap: break-word;
              font-size: ${fontSize * 0.9}px;
              line-height: 1.2;
              margin-bottom: 2px;
            }
            
            .skill-item:before {
              content: "•";
              margin-right: 4px;
              color: black;
              flex-shrink: 0;
            }
            
            /* Template-specific styles */
            .printable-resume.classic .header {
              border-bottom: 2px solid ${titleColor};
              page-break-after: avoid;
              margin-bottom: ${Math.min(sectionSpacing, 16)}px;
            }
            
            .printable-resume.classic .section-title {
              border-bottom: 1px solid ${titleColor};
              padding-bottom: 4px;
              page-break-after: avoid;
            }
            
            .printable-resume.modern .modern-header,
            .printable-resume.modern .header {
              background: ${titleColor};
              color: white;
              margin: -0.5in -0.5in ${sectionSpacing}px -0.5in;
              padding: 20px 0.5in;
              border-radius: 0 0 8px 8px;
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            
            .printable-resume.modern .modern-header h1,
            .printable-resume.modern .header h1 {
              color: white;
            }
            
            .printable-resume.modern .modern-header .contact,
            .printable-resume.modern .header .contact {
              color: rgba(255, 255, 255, 0.9);
            }
            
            .printable-resume.modern .section-title {
              color: ${titleColor};
              border-left: 4px solid ${titleColor};
              border-bottom: none;
              padding-left: 12px;
            }
            
            .printable-resume.creative .creative-header,
            .printable-resume.creative .header {
              background: linear-gradient(135deg, ${titleColor} 0%, ${subtitleColor} 100%);
              background-color: ${titleColor};
              color: white;
              margin: -0.5in -0.5in ${sectionSpacing}px -0.5in;
              padding: 24px 0.5in;
              border-radius: 0 0 12px 12px;
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            
            .printable-resume.creative .creative-header h1,
            .printable-resume.creative .header h1 {
              color: white;
            }
            
            .printable-resume.creative .creative-header .contact,
            .printable-resume.creative .header .contact {
              color: rgba(255, 255, 255, 0.95);
            }
            
            .printable-resume.creative .section-title {
              color: ${titleColor};
              background: linear-gradient(90deg, ${titleColor}, ${subtitleColor});
              background-size: 100% 2px;
              background-repeat: no-repeat;
              background-position: bottom;
              border-bottom: none;
              padding-bottom: 4px;
            }
          </style>
        </head>
        <body>
          ${clonedResume.outerHTML}
        </body>
        </html>
      `;

        // Write content to iframe
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(printHTML);
        iframeDoc.close();

        // Wait for content to load, then trigger print
        setTimeout(() => {
          try {
            let printCompleted = false;

            const handleAfterPrint = () => {
              if (!printCompleted) {
                printCompleted = true;

                // Clean up iframe
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                }

                // Navigate to thank you page
                message.success("Resume downloaded successfully!");
                setTimeout(() => {
                  if (window.location.pathname.includes("/app/")) {
                    window.location.href = "/app/thank-you";
                  } else {
                    // Fallback navigation
                    window.location.href = "/app/thank-you";
                  }
                }, 1000);
              }
            };

            // Listen for afterprint event on iframe
            iframe.contentWindow.addEventListener("afterprint", handleAfterPrint);

            // Focus the iframe and trigger print
            iframe.contentWindow.focus();
            iframe.contentWindow.print();

            // Fallback timeout to clean up iframe
            setTimeout(() => {
              if (!printCompleted) {
                printCompleted = true;

                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                }
              }
            }, 10000);
          } catch (printErr) {
            console.error("Print error:", printErr);
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
            message.error("Print failed. Please try again.");
          }
        }, 1000);
      } catch (err) {
        console.error("Print error:", err);
        message.error("Print failed. Please try again.");
      }
    };

    // Handle download with custom styles - expose this via props if needed
    const handleDownloadWithCustomStyles = (onDownload) => {
      if (onDownload) {
        const customStyles = {
          fontSize,
          sectionSpacing,
          titleColor,
          subtitleColor,
        };

        onDownload(customStyles, customSectionOrder);
      }
    };

    // Get resume data with custom section order applied
    const getCustomOrderedResumeData = () => {
      if (!resumeData?.parsedData || !customSectionOrder) {
        return resumeData;
      }

      const sortedData = sortResumeForDisplay(resumeData.parsedData);
      const orderedData = {};

      // Apply custom order
      customSectionOrder.forEach((sectionId) => {
        if (sortedData[sectionId]) {
          orderedData[sectionId] = sortedData[sectionId];
        }
      });

      // Add any missing sections
      Object.keys(sortedData).forEach((key) => {
        if (!orderedData[key]) {
          orderedData[key] = sortedData[key];
        }
      });

      return { ...resumeData, parsedData: orderedData };
    };

    // Render draggable section item
    const renderSectionItem = (section, index) => {
      const isPersonalInfo = section.id === "personalInfo";

      return (
        <Draggable key={section.id} draggableId={section.id} index={index} isDragDisabled={isPersonalInfo}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              style={{
                marginBottom: "8px",
                ...provided.draggableProps.style,
              }}
            >
              <Card
                size="small"
                style={{
                  border: snapshot.isDragging ? "2px solid #1890ff" : "1px solid #d9d9d9",
                  backgroundColor: snapshot.isDragging ? "#f0f9ff" : "white",
                  opacity: isPersonalInfo ? 0.7 : 1,
                  cursor: isPersonalInfo ? "not-allowed" : "move",
                  transition: "all 0.2s ease",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div
                    {...provided.dragHandleProps}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      color: isPersonalInfo ? "#ccc" : "#666",
                      cursor: isPersonalInfo ? "not-allowed" : "grab",
                    }}
                  >
                    <DragOutlined />
                  </div>

                  <div style={{ flex: 1 }}>
                    <Space>
                      <span style={{ fontSize: "16px" }}>{section.icon}</span>
                      <div>
                        <Text strong style={{ color: isPersonalInfo ? "#999" : "inherit" }}>
                          {section.title}
                        </Text>
                      </div>
                    </Space>
                  </div>

                  {isPersonalInfo && (
                    <Tooltip title="Personal Information always stays at the top">
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        Fixed
                      </Text>
                    </Tooltip>
                  )}
                </div>
              </Card>
            </div>
          )}
        </Draggable>
      );
    };

    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header Controls */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "16px",
            paddingBottom: "12px",
          }}
        >
          {resumeData?.coverLetter?.generatedContent && (
            <Segmented
              options={[
                {
                  label: "Resume Preview",
                  value: "resume",
                  icon: <FileOutlined />,
                },
                {
                  label: "Cover Letter Preview",
                  value: "coverLetter",
                  icon: <FileTextOutlined />,
                },
              ]}
              value={viewMode}
              onChange={setViewMode}
              size="large"
            />
          )}
        </div>

        {/* Main Content */}
        <div
          style={{
            flex: 1,
            display: "flex",
            gap: "20px",
            minHeight: 0,
            height: "calc(100vh - 200px)",
            overflow: "hidden",
          }}
        >
          {/* Customization Panel - Sticky - Only show for resume mode */}
          {viewMode === "resume" && (
            <div
              style={{
                width: "320px",
                flexShrink: 0,
                position: "sticky",
                top: "20px",
                alignSelf: "flex-start",
                maxHeight: "calc(100vh - 40px)",
                overflowY: "auto",
              }}
            >
              <Card
                title={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Space>
                      <SettingOutlined />
                      <span>Customize Resume</span>
                    </Space>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={handleReset}
                      size="small"
                      type="default"
                      style={{
                        padding: "4px 8px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "4px",
                      }}
                    >
                      Reset All
                    </Button>
                  </div>
                }
                size="small"
                style={{
                  height: "fit-content",
                  maxHeight: "100%",
                  overflowY: "visible",
                }}
              >
                <Tabs activeKey={activeTab} onChange={setActiveTab} type="card" size="small">
                  <TabPane
                    tab={
                      <Space>
                        <DragOutlined />
                        <span>Order</span>
                      </Space>
                    }
                    key="1"
                  >
                    <Text type="secondary" style={{ display: "block", marginBottom: "16px" }}>
                      Drag sections to reorder them in your resume. Personal Information always stays at the top.
                    </Text>

                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="sections">
                        {(provided, snapshot) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={{
                              backgroundColor: snapshot.isDraggingOver ? "#f6ffed" : "transparent",
                              borderRadius: "6px",
                              padding: snapshot.isDraggingOver ? "8px" : "0",
                              transition: "all 0.2s ease",
                            }}
                          >
                            {orderedSections.map((section, index) => renderSectionItem(section, index))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </TabPane>

                  <TabPane
                    tab={
                      <Space>
                        <SettingOutlined />
                        <span>Styling</span>
                      </Space>
                    }
                    key="2"
                  >
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <div style={{ marginBottom: "16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                          <FileTextOutlined style={{ color: "#1890ff" }} />
                          <Text strong>Font Family</Text>
                        </div>
                        <Select
                          value={fontFamily}
                          onChange={(value) => updateTemplateCustomization("fontFamily", value)}
                          style={{ width: "100%" }}
                          options={AVAILABLE_FONTS}
                          optionRender={(option) => (
                            <span style={{ fontFamily: option.value }}>{option.label}</span>
                          )}
                        />
                      </div>

                      <Divider />

                      <div style={{ marginBottom: "16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                          <FontSizeOutlined style={{ color: "#1890ff" }} />
                          <Text strong>Font Size</Text>
                        </div>
                        <Text style={{ fontSize: "12px", color: "#666", marginBottom: "8px", display: "block" }}>
                          Font Size: {fontSize}px
                        </Text>
                        <Slider
                          min={10}
                          max={20}
                          value={fontSize}
                          onChange={(value) => updateTemplateCustomization("fontSize", value)}
                          marks={{
                            10: "10px",
                            12: "12px",
                            14: "14px",
                            16: "16px",
                            18: "18px",
                            20: "20px",
                          }}
                        />
                      </div>

                      <Divider />

                      <div style={{ marginBottom: "16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                          <ColumnHeightOutlined style={{ color: "#1890ff" }} />
                          <Text strong>Spacing</Text>
                        </div>
                        <Text style={{ fontSize: "12px", color: "#666", marginBottom: "8px", display: "block" }}>
                          Spacing: {sectionSpacing}px
                        </Text>
                        <Slider
                          min={0}
                          max={24}
                          value={sectionSpacing}
                          onChange={(value) => updateTemplateCustomization("sectionSpacing", value)}
                          marks={{
                            0: "0px",
                            6: "6px",
                            12: "12px",
                            18: "18px",
                            24: "24px",
                          }}
                        />
                      </div>

                      <Divider />

                      <div style={{ marginBottom: "16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                          <BgColorsOutlined style={{ color: "#1890ff" }} />
                          <Text strong>Colors</Text>
                        </div>

                        {/* Preset Color Options for MinimalistPro */}
                        {selectedTemplate?.id === "minimalist_pro" && (
                          <div style={{ marginBottom: "16px" }}>
                            <Text
                              style={{ fontSize: "13px", fontWeight: "500", marginBottom: "8px", display: "block" }}
                            >
                              Quick Color Presets
                            </Text>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              {[
                                { color: "#0d9488", name: "Teal" },
                                { color: "#1f2937", name: "Black" },
                                { color: "#16a34a", name: "Green" },
                                { color: "#2563eb", name: "Blue" },
                                { color: "#6b7280", name: "Gray" },
                              ].map((preset) => (
                                <Tooltip key={preset.color} title={preset.name}>
                                  <div
                                    onClick={() => {
                                      updateTemplateCustomization("titleColor", preset.color);
                                    }}
                                    style={{
                                      width: "32px",
                                      height: "32px",
                                      borderRadius: "6px",
                                      backgroundColor: preset.color,
                                      cursor: "pointer",
                                      border: titleColor === preset.color ? "3px solid #1890ff" : "2px solid #e5e7eb",
                                      transition: "all 0.2s ease",
                                      boxShadow: titleColor === preset.color ? "0 0 0 2px rgba(24, 144, 255, 0.2)" : "none",
                                    }}
                                  />
                                </Tooltip>
                              ))}
                            </div>
                          </div>
                        )}

                        <div style={{ marginBottom: "16px" }}>
                          <Text
                            style={{ fontSize: "13px", fontWeight: "500", marginBottom: "8px", display: "block" }}
                          >
                            Section Titles Color
                          </Text>
                          <ColorPicker
                            value={titleColor}
                            onChange={(color) => {
                              const hexValue = typeof color === "string" ? color : color.toHexString();
                              updateTemplateCustomization("titleColor", hexValue);
                            }}
                            showText
                            size="small"
                            allowClear={false}
                            disabledAlpha
                          />
                        </div>

                        <div style={{ marginBottom: "16px" }}>
                          <Text
                            style={{ fontSize: "13px", fontWeight: "500", marginBottom: "8px", display: "block" }}
                          >
                            Subtitles Color
                          </Text>
                          <ColorPicker
                            value={subtitleColor}
                            onChange={(color) => {
                              const hexValue = typeof color === "string" ? color : color.toHexString();
                              updateTemplateCustomization("subtitleColor", hexValue);
                            }}
                            showText
                            size="small"
                            allowClear={false}
                            disabledAlpha
                          />
                        </div>
                      </div>
                    </Space>
                  </TabPane>

                  <TabPane
                    tab={
                      <Space>
                        <UserOutlined />
                        <span>Edit Photo</span>
                      </Space>
                    }
                    key="3"
                  >
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Text type="secondary" style={{ display: "block", marginBottom: "16px", fontSize: "13px" }}>
                        {profilePhotoUrl
                          ? "Update your profile photo. It will be displayed on your resume."
                          : "Upload a professional profile photo to make your resume stand out."}
                      </Text>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "16px",
                          padding: "16px",
                          backgroundColor: "#f8fafc",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        {profilePhotoUrl && (
                          <div
                            style={{
                              width: "120px",
                              height: "120px",
                              borderRadius: "50%",
                              overflow: "hidden",
                              backgroundColor: "#e2e8f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "3px solid #1890ff",
                              boxShadow: "0 4px 12px rgba(24, 144, 255, 0.15)",
                            }}
                          >
                            <img
                              src={profilePhotoUrl}
                              alt="Profile"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        )}

                        <ProfilePhotoUpload
                          resumeId={resumeId}
                          currentPhotoUrl={profilePhotoUrl}
                          onPhotoUploaded={handlePhotoUploaded}
                          showModal={false}
                          mode={profilePhotoUrl ? "edit" : "upload"}
                        />
                      </div>
                    </Space>
                  </TabPane>
                </Tabs>
              </Card>
            </div>
          )}

          {/* Resume Preview - No Scroll */}
          <div
            style={{
              flex: 1,
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              padding: "20px",
              minHeight: 0,
              overflow: "hidden",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {resumeData && (
              <div
                style={{
                  flex: 1,
                  width: "100%",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  overflow: "hidden",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {viewMode === "resume"
                  ? (() => {
                      const TemplateComponent = TEMPLATE_MAPPING[selectedTemplate?.id] || ClassicATSTemplate;
                      const pdfData = prepareResumeDataForPDF(resumeData);

                      if (!pdfData) {
                        return (
                          <div style={{ padding: "40px", textAlign: "center" }}>
                            <Text type="secondary">No resume data available</Text>
                          </div>
                        );
                      }

                      // Apply custom section order if available
                      let orderedPdfData = pdfData;
                      let mappedSectionOrder = null;
                      if (customSectionOrder) {
                        // Map section IDs from SectionOrderingUtils to template section IDs
                        const sectionIdMapping = {
                          personalInfo: "personalInfo", // Personal info is handled separately in templates
                          summary: "summary",
                          experience: "workExperience", // Map experience to workExperience
                          workExperience: "workExperience", // Also handle workExperience directly
                          education: "education",
                          skills: "skills",
                          skillsLanguages: "skills", // Map skillsLanguages to skills to avoid duplicates
                          projects: "projects",
                          certifications: "certifications",
                          achievements: "achievements",
                          awards: "awards",
                          tools: "tools",
                          languages: "languages",
                          volunteering: "volunteering",
                          interests: "interests",
                          industryExpertise: "industryExpertise",
                          publications: "publications",
                          references: "references",
                        };

                        // Map and remove duplicates to ensure each section only appears once
                        // Custom section IDs (not in sectionIdMapping) are passed through as-is
                        const seen = new Set();
                        mappedSectionOrder = customSectionOrder
                          .filter((sectionId) => sectionId !== "personalInfo") // Personal info is always at top in templates
                          .map((sectionId) => sectionIdMapping[sectionId] || sectionId)
                          .filter((sectionId) => {
                            if (!sectionId || seen.has(sectionId)) return false;
                            seen.add(sectionId);
                            return true;
                          });
                      }

                      return (
                        <>
                          <style>
                            {`
                        .react-pdf__Document {
                          background: transparent !important;
                          margin: 0 !important;
                          padding: 0 !important;
                        }
                        .react-pdf__Page {
                          margin: 0 !important;
                          padding: 0 !important;
                          display: block !important;
                        }
                        .react-pdf__Page__canvas {
                          margin: 0 !important;
                          padding: 0 !important;
                          display: block !important;
                        }
                      `}
                          </style>
                          <BlobProvider
                            document={
                              <TemplateComponent
                                resumeData={orderedPdfData}
                                customStyles={{
                                  fontSize,
                                  sectionSpacing,
                                  titleColor,
                                  subtitleColor,
                                  fontFamily,
                                }}
                                customSectionOrder={mappedSectionOrder}
                              />
                            }
                          >
                            {({ blob, url, loading, error }) => {
                              const cacheKey = `${selectedTemplate?.id}_${JSON.stringify(
                                customizations[selectedTemplate?.id] || {}
                              )}_${JSON.stringify(customSectionOrder || [])}`;

                              // Cache the generated PDF URL for future use
                              useEffect(() => {
                                if (url && !loading && !error && !cachedPDFs[cacheKey]) {
                                  setCachedPDFs((prev) => ({
                                    ...prev,
                                    [cacheKey]: url,
                                  }));
                                }
                              }, [url, loading, error, cacheKey]);

                              // Check if we have preloaded URL or cached version first
                              const cachedUrl = cachedPDFs[cacheKey];
                              const displayUrl = preloadedUrl || cachedUrl || url;

                              // If we have preloaded URL, show PDFViewer directly
                              if (preloadedUrl) {
                                return (
                                  <PDFViewer
                                    style={{
                                      width: "min(210mm, 98vw)",
                                      height: "min(297mm, 85vh)",
                                      border: "none",
                                      backgroundColor: "transparent",
                                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                      margin: 0,
                                      marginTop: "0px",
                                      padding: 0,
                                    }}
                                    showToolbar={false}
                                  >
                                    <TemplateComponent
                                      resumeData={orderedPdfData}
                                      customStyles={{
                                        fontSize,
                                        sectionSpacing,
                                        titleColor,
                                        subtitleColor,
                                      }}
                                      customSectionOrder={mappedSectionOrder}
                                    />
                                  </PDFViewer>
                                );
                              }

                              if (loading && !cachedUrl) {
                                return (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      flexDirection: "column",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      gap: "20px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        position: "relative",
                                        width: "100%",
                                        maxWidth: "800px",
                                      }}
                                    >
                                      <ResumePreviewSkeleton />
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "50%",
                                          left: "50%",
                                          transform: "translate(-50%, -50%)",
                                          background: "rgba(255, 255, 255, 0.9)",
                                          padding: "20px",
                                          borderRadius: "8px",
                                          textAlign: "center",
                                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                        }}
                                      >
                                        <Spin
                                          indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                                          size="large"
                                        />
                                        <Text type="secondary" style={{ display: "block", marginTop: "12px" }}>
                                          Generating resume preview...
                                        </Text>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }

                              if (error) {
                                return (
                                  <div style={{ padding: "40px", textAlign: "center" }}>
                                    <Text type="danger">Error generating PDF: {error.message}</Text>
                                    <br />
                                    <Button
                                      type="primary"
                                      onClick={() => window.location.reload()}
                                      style={{ marginTop: "16px" }}
                                    >
                                      Reload Page
                                    </Button>
                                  </div>
                                );
                              }

                              if (displayUrl) {
                                // Show PDFViewer directly
                                return (
                                  <PDFViewer
                                    style={{
                                      width: "min(210mm, 98vw)",
                                      height: "min(297mm, 85vh)",
                                      border: "none",
                                      backgroundColor: "transparent",
                                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                      margin: 0,
                                      marginTop: "0px",
                                      padding: 0,
                                    }}
                                    showToolbar={false}
                                  >
                                    <TemplateComponent
                                      resumeData={orderedPdfData}
                                      customStyles={{
                                        fontSize,
                                        sectionSpacing,
                                        titleColor,
                                        subtitleColor,
                                      }}
                                      customSectionOrder={mappedSectionOrder}
                                    />
                                  </PDFViewer>
                                );
                              }
                            }}
                          </BlobProvider>
                        </>
                      );
                    })()
                  : (() => {
                      // Cover Letter Preview
                      const CoverLetterTemplate =
                        COVER_LETTER_TEMPLATE_MAPPING[selectedTemplate?.id] || ClassicCoverLetter;
                      const coverLetterData = resumeData?.coverLetter;

                      if (!coverLetterData?.generatedContent) {
                        return (
                          <div style={{ padding: "40px", textAlign: "center" }}>
                            <Text type="secondary">No cover letter available</Text>
                          </div>
                        );
                      }

                      return (
                        <>
                          <style>
                            {`
                        .react-pdf__Document {
                          background: transparent !important;
                          margin: 0 !important;
                          padding: 0 !important;
                        }
                        .react-pdf__Page {
                          margin: 0 !important;
                          padding: 0 !important;
                          display: block !important;
                        }
                        .react-pdf__Page__canvas {
                          margin: 0 !important;
                          padding: 0 !important;
                          display: block !important;
                        }
                      `}
                          </style>
                          <PDFViewer
                            style={{
                              width: "min(210mm, 98vw)",
                              height: "min(297mm, 85vh)",
                              border: "none",
                              backgroundColor: "transparent",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              margin: 0,
                              marginTop: "0px",
                              padding: 0,
                            }}
                            showToolbar={false}
                          >
                            <CoverLetterTemplate resumeData={resumeData} coverLetterData={coverLetterData} customStyles={getTemplateCustomizations()} />
                          </PDFViewer>
                        </>
                      );
                    })()}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default CustomizableResumePreview;
